import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Volume2, VolumeX } from 'lucide-react';
import {
  deriveMancala,
  isGuestId,
  mancalaPitSlot,
  mancalaStore,
  replayStates,
  sowMancala,
  type GameMeta,
  type MancalaBoard,
  type MancalaMove,
  type MancalaPlayer,
} from '@awwyeah/shared';
import { GameHeader } from '../components/GameHeader';
import { ReplayControls, ReplayEntryButtons } from '../components/ReplayControls';
import { SideLine } from '../components/SideLine';
import { createGameMeta, nowIso } from '../gameOps';
import { initialScrubState, scrubReducer } from '../replayState';
import { useMancalaMoves } from '../useGameMoves';
import { useGamesRegistry } from '../useGamesRegistry';
import { useGameRoster } from '../useHouseholdRoster';
import { beadSpot } from './beads';
import { buzz, playChirp, playClack, unlockAudio } from './sound';
import { publishRematch } from '../rematch';

/** Time between beads while sowing. Slow enough for a kid to count along. */
const STEP_MS = 210;
/** Replay waits long enough for a full sow to play out between moves. */
const REPLAY_TICK_MS = 1500;

const SOUND_KEY = 'awwyeah.mancala.sound';
const sharedKey = (gameId: string): string => `awwyeah.mancala.shared.${gameId}`;

function readPref(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // per-device nicety only
  }
}

const WOOD: React.CSSProperties = {
  backgroundColor: '#a8662f',
  backgroundImage: [
    'radial-gradient(ellipse 120% 40% at 20% 30%, rgba(255,214,160,.16), transparent 60%)',
    'repeating-linear-gradient(88deg, rgba(60,25,5,.10) 0 1px, transparent 1px 7px)',
    'repeating-linear-gradient(91deg, rgba(255,220,170,.07) 0 2px, transparent 2px 13px)',
    'repeating-radial-gradient(ellipse 30% 260% at 70% 50%, rgba(70,32,8,.16) 0 2px, transparent 2px 11px)',
    'linear-gradient(180deg, #b8763c, #9c5a28 55%, #8a4c20)',
  ].join(','),
  boxShadow:
    '0 10px 24px rgba(40,18,4,.35), inset 0 2px 0 rgba(255,225,180,.35), inset 0 -3px 0 rgba(50,20,4,.35)',
};

const CUP: React.CSSProperties = {
  backgroundImage:
    'radial-gradient(ellipse 80% 70% at 50% 40%, #6b3a1a, #4a250e 70%, #3a1c09), repeating-linear-gradient(90deg, rgba(0,0,0,.08) 0 1px, transparent 1px 6px)',
  boxShadow:
    'inset 0 6px 12px rgba(0,0,0,.6), inset 0 -2px 4px rgba(255,200,140,.12), 0 1px 0 rgba(255,225,180,.35)',
};

interface Anim {
  board: MancalaBoard;
  /** Slot that just got a bead (gets the drop animation). */
  drop: number | null;
  from: number;
}

function Beads({
  slot,
  count,
  cap,
  dropping,
  spreadX,
  spreadY,
}: {
  slot: number;
  count: number;
  cap: number;
  dropping: boolean;
  spreadX?: number;
  spreadY?: number;
}): JSX.Element {
  const shown = Math.min(count, 60);
  return (
    <>
      {Array.from({ length: shown }, (_, i) => {
        const spot = beadSpot(slot, i, cap, spreadX, spreadY);
        const isNew = dropping && i === shown - 1;
        return (
          <span
            key={i}
            aria-hidden="true"
            className="pointer-events-none absolute rounded-full"
            style={{
              left: `${spot.x}%`,
              top: `${spot.y}%`,
              width: 'var(--bead)',
              height: 'var(--bead)',
              transform: 'translate(-50%, -50%)',
              backgroundImage: `radial-gradient(circle at 34% 30%, rgba(255,255,255,.95) 0 10%, ${spot.color[0]} 34%, ${spot.color[1]} 100%)`,
              boxShadow: '0 1.5px 1.5px rgba(0,0,0,.55)',
              animation: isNew
                ? 'awwyeah-bead-drop 220ms cubic-bezier(.3,1.5,.6,1) both'
                : undefined,
            }}
          />
        );
      })}
    </>
  );
}

/**
 * Mancala (Kalah). The board is upright for phones: your pits run down the right side into
 * your store at the bottom; theirs run up the left into their store at the top. Opposite
 * pits share a row, so captures read at a glance.
 *
 * Taps: tap a pit to see where its last bead will land (and whether it's a free turn or a
 * capture), tap it again to sow. Pits that would give a free turn wear a small star.
 * "Same phone" lets two people take turns on one device; those moves don't push anyone.
 */
export function MancalaGame({ meta, userId }: { meta: GameMeta; userId: string }): JSX.Element {
  const navigate = useNavigate();
  const registries = useGamesRegistry();
  const roster = useGameRoster(meta.guestNames);
  const playerIds = meta.playerIds;
  const { movesArray, moves, state } = useMancalaMoves(meta.id, playerIds);

  const derive = useCallback(
    (prefix: readonly MancalaMove[]) => deriveMancala(prefix, playerIds),
    [playerIds],
  );
  const frames = useMemo(() => replayStates(moves, derive), [moves, derive]);
  const [scrub, dispatchScrub] = useReducer(scrubReducer, initialScrubState);

  const prevMoveCountRef = useRef(moves.length);
  useEffect(() => {
    if (moves.length > prevMoveCountRef.current) dispatchScrub({ type: 'new-move-arrived' });
    prevMoveCountRef.current = moves.length;
  }, [moves.length]);

  useEffect(() => {
    if (scrub.mode !== 'scrubbing' || !scrub.playing) return undefined;
    const timer = setTimeout(
      () => dispatchScrub({ type: 'tick', maxFrame: frames.length - 1 }),
      REPLAY_TICK_MS,
    );
    return () => clearTimeout(timer);
  }, [scrub, frames.length]);

  const shown = scrub.mode === 'scrubbing' ? (frames[scrub.frame]?.state ?? state) : state;

  const [soundOn, setSoundOn] = useState(() => readPref(SOUND_KEY, true));
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;
  const [sharedPhone, setSharedPhone] = useState(() => readPref(sharedKey(meta.id), false));

  // ---- Sowing animation: whenever the shown position advances by exactly one move, play
  // that move bead by bead from the previous position. Anything else (opening the game,
  // jumping around the replay) just snaps.
  const [anim, setAnim] = useState<Anim | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const lastShownRef = useRef({ applied: shown.appliedMoves, board: shown.board });
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const skipRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const now = shownRef.current;
    const prev = lastShownRef.current;
    lastShownRef.current = { applied: now.appliedMoves, board: now.board };
    const lm = now.lastMove;
    if (now.appliedMoves !== prev.applied + 1 || !lm) {
      setAnim(null);
      return undefined;
    }
    const pit = lm.from - (lm.player === 0 ? 0 : 7);
    const sow = sowMancala(prev.board, lm.player, pit);
    if (!sow) return undefined;

    let i = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (): void => {
      if (timer) clearTimeout(timer);
      skipRef.current = null;
      setAnim(null);
      const msg = sow.ended
        ? null
        : sow.captured > 0
          ? `Captured ${sow.captured}!`
          : sow.extraTurn
            ? 'Go again!'
            : null;
      if (msg) {
        setFlash(msg);
        if (soundRef.current) playChirp();
        timer = setTimeout(() => setFlash(null), 1300);
      }
    };
    const step = (): void => {
      const board = sow.steps[i]!;
      const before = i === 0 ? prev.board : sow.steps[i - 1]!;
      let drop: number | null = null;
      let changed = 0;
      board.forEach((n, s) => {
        if (n !== before[s]) changed += 1;
        if (n === (before[s] ?? 0) + 1) drop = s;
      });
      if (changed !== 1) drop = null; // capture / sweep steps move many at once
      setAnim({ board, drop, from: sow.from });
      if (soundRef.current && i > 0) {
        playClack(changed > 1 ? 'capture' : drop === 6 || drop === 13 ? 'store' : 'pit');
      }
      if (changed > 1) buzz(25);
      i += 1;
      if (i < sow.steps.length) timer = setTimeout(step, changed > 1 ? 420 : STEP_MS);
      else timer = setTimeout(finish, 260);
    };
    skipRef.current = finish;
    step();
    return () => {
      if (timer) clearTimeout(timer);
      skipRef.current = null;
    };
    // Keyed on the position actually changing, not on every re-derive.
  }, [shown.appliedMoves, scrub.mode]);

  const board = anim?.board ?? shown.board;
  const animating = anim !== null;

  // ---- Stamp "done" on the registry, same as the other kinds.
  useEffect(() => {
    if (!meta.playerIds.includes(userId) || isGuestId(userId)) return;
    if (meta.status === 'done') return;
    if (state.winner === null && !state.isDraw) return;
    const winnerId = state.winner !== null ? meta.playerIds[state.winner] : undefined;
    const at = nowIso();
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      ...(winnerId ? { winnerId } : {}),
      finishedAt: at,
      updatedBy: userId,
      updatedAt: at,
    });
  }, [meta, userId, registries.map, state.winner, state.isDraw]);

  const mySeatRaw = meta.playerIds.indexOf(userId);
  const isPlayer = mySeatRaw !== -1;
  const me: MancalaPlayer = mySeatRaw === 1 ? 1 : 0;
  const them: MancalaPlayer = me === 0 ? 1 : 0;
  const finished = state.winner !== null || state.isDraw;
  const live = scrub.mode === 'live';
  const myTurn = isPlayer && state.turn === me;
  const sharing = isPlayer && sharedPhone;
  /** Whose pits the person holding this phone may tap right now. */
  const actor: MancalaPlayer | null =
    !isPlayer || finished || !live || state.turn === null
      ? null
      : sharing
        ? state.turn
        : myTurn
          ? me
          : null;

  const nameOf = (seat: MancalaPlayer): string => roster.getMember(meta.playerIds[seat]).name;
  const colorOf = (seat: MancalaPlayer): string => roster.getMember(meta.playerIds[seat]).color;

  const statusLine = (): string => {
    const score = `${state.board[mancalaStore(me)]}–${state.board[mancalaStore(them)]}`;
    if (state.winner !== null) {
      if (!isPlayer) return `${nameOf(state.winner)} won`;
      return state.winner === me ? `You won ${score}` : `${nameOf(them)} won ${score}`;
    }
    if (state.isDraw) return `Draw ${score}`;
    if (state.turn === null) return '';
    if (!isPlayer || sharing)
      return state.turn === me && isPlayer ? 'Your move' : `${nameOf(state.turn)}'s move`;
    return state.turn === me ? 'Your move' : `${nameOf(them)}'s move`;
  };

  // ---- Selection + landing preview.
  const [selected, setSelected] = useState<number | null>(null);
  useEffect(() => setSelected(null), [state.appliedMoves, actor]);
  const preview = useMemo(
    () => (selected === null || actor === null ? null : sowMancala(state.board, actor, selected)),
    [selected, actor, state.board],
  );
  const freeTurnPits = useMemo(() => {
    const out = new Set<number>();
    if (actor === null) return out;
    for (let p = 0; p < 6; p += 1) {
      const r = sowMancala(state.board, actor, p);
      if (r?.extraTurn) out.add(mancalaPitSlot(actor, p));
    }
    return out;
  }, [actor, state.board]);

  const sow = (pit: number): void => {
    if (actor === null || animating) return;
    if (!sowMancala(state.board, actor, pit)) return;
    buzz(12);
    movesArray.push([
      {
        pit,
        playerId: meta.playerIds[actor],
        at: nowIso(),
        ...(sharing ? { sharedPhone: true } : {}),
      },
    ]);
    setSelected(null);
  };

  const tapPit = (slot: number): void => {
    unlockAudio();
    if (animating) {
      skipRef.current?.();
      return;
    }
    if (actor === null) return;
    const pit = slot - (actor === 0 ? 0 : 7);
    if (pit < 0 || pit > 5 || (state.board[slot] ?? 0) === 0) return;
    if (selected === pit) sow(pit);
    else {
      setSelected(pit);
      buzz(6);
    }
  };

  const rematch = (): void => {
    const opponentId = meta.playerIds[them];
    const fresh = createGameMeta({
      kind: 'mancala',
      starterId: userId,
      starterName: roster.getMember(userId).name,
      opponentId,
      opponentName: roster.getMember(opponentId).name,
    });
    publishRematch(registries.map, fresh, meta);
    navigate(`/games/${fresh.id}`);
  };

  const previewText = (): string | null => {
    if (!preview || actor === null) return null;
    const n = state.board[preview.from] ?? 0;
    const beads = `${n} bead${n === 1 ? '' : 's'}`;
    if (preview.extraTurn) return `${beads} · ends in the store, go again! Tap again to sow`;
    if (preview.captured > 0) return `${beads} · captures ${preview.captured}! Tap again to sow`;
    return `${beads} · tap again to sow`;
  };

  // ---- Layout, like sitting at a table: your row along the bottom, sown left → right
  // into your store on the right; their row across the top, right → left into their
  // store on the left. Opposite pits share a column, so captures read at a glance.
  const cols = [0, 1, 2, 3, 4, 5];
  const badgeStyle: React.CSSProperties = {
    background: 'rgba(255,240,215,.92)',
    color: '#4a250e',
    boxShadow: '0 1px 2px rgba(0,0,0,.35)',
  };

  const pitButton = (slot: number, owner: MancalaPlayer): JSX.Element => {
    const count = board[slot] ?? 0;
    const pit = slot - (owner === 0 ? 0 : 7);
    const near = owner === me;
    const tappable = actor === owner && count > 0 && !animating;
    const isSelected = tappable && selected === pit;
    const isLanding = preview?.landing === slot && !animating;
    const isFrom = anim?.from === slot;
    const star = tappable && freeTurnPits.has(slot);
    return (
      <div
        key={slot}
        className={`flex items-center gap-1 ${near ? 'flex-col' : 'flex-col-reverse'}`}
      >
        <button
          type="button"
          data-testid="mancala-pit"
          data-slot={slot}
          data-count={count}
          data-owner={owner}
          aria-label={`${near && isPlayer ? 'Your' : `${nameOf(owner)}'s`} pit, ${count} bead${count === 1 ? '' : 's'}`}
          aria-pressed={isSelected}
          onClick={() => tapPit(slot)}
          className="relative aspect-[5/8] w-full rounded-[50%] transition-transform duration-150"
          style={{
            ...CUP,
            transform: isSelected ? `translateY(${near ? -4 : 4}px) scale(1.06)` : undefined,
            outline: isSelected
              ? '3px solid #ffe2a8'
              : isLanding
                ? '2.5px dashed #ffe2a8'
                : isFrom
                  ? '2px solid rgba(255,226,168,.55)'
                  : undefined,
            outlineOffset: 2,
            cursor: tappable ? 'pointer' : 'default',
          }}
        >
          <Beads
            slot={slot}
            count={count}
            cap={9}
            dropping={anim?.drop === slot}
            spreadX={30}
            spreadY={34}
          />
        </button>
        <span className="flex h-[18px] items-center gap-0.5" aria-hidden="true">
          <span
            className="rounded-full px-1.5 text-[11px] font-bold leading-[18px] tabular-nums"
            style={badgeStyle}
          >
            {count}
          </span>
          {star && (
            <span
              className="text-xs"
              style={{ color: '#ffe2a8', textShadow: '0 1px 2px #000' }}
              title="Free turn"
            >
              ★
            </span>
          )}
        </span>
      </div>
    );
  };

  const storeBox = (owner: MancalaPlayer): JSX.Element => {
    const slot = mancalaStore(owner);
    const count = board[slot] ?? 0;
    const isLanding = preview?.landing === slot && !animating;
    const label = owner === me && isPlayer ? 'Your store' : `${nameOf(owner)}'s store`;
    return (
      <div
        data-testid="mancala-store"
        data-slot={slot}
        data-count={count}
        className="relative row-span-2 my-[22px] rounded-[999px]"
        style={{
          ...CUP,
          outline: isLanding ? '2.5px dashed #ffe2a8' : undefined,
          outlineOffset: 2,
        }}
        onClick={() => animating && skipRef.current?.()}
        role="img"
        aria-label={`${label}, ${count} bead${count === 1 ? '' : 's'}`}
      >
        <Beads
          slot={slot}
          count={count}
          cap={22}
          dropping={anim?.drop === slot}
          spreadX={30}
          spreadY={40}
        />
        <span
          className={`absolute left-1/2 -translate-x-1/2 rounded-full px-1.5 text-xs font-bold leading-[20px] tabular-nums ${
            owner === me ? '-bottom-[22px]' : '-top-[22px]'
          }`}
          style={badgeStyle}
        >
          {count}
        </span>
      </div>
    );
  };

  const nameChip = (owner: MancalaPlayer): JSX.Element => (
    <div
      className={`flex items-center gap-1.5 px-2 text-sm ${owner === me ? 'justify-end' : 'justify-start'}`}
    >
      <span
        className="h-3 w-3 rounded-full ring-1 ring-line-strong"
        style={{ backgroundColor: colorOf(owner) }}
      />
      <span className={state.turn === owner && !finished ? 'font-semibold text-fg' : 'text-fg-2'}>
        {owner === me && isPlayer ? 'You' : nameOf(owner)}
      </span>
      {state.turn === owner && !finished && (
        <span className="text-fg-3">· {owner === me && isPlayer ? 'your' : 'their'} move</span>
      )}
    </div>
  );

  const maxFrame = frames.length - 1;
  const frameAt = frames[scrub.frame];
  const tip = previewText();

  return (
    <div className="flex flex-col gap-3 py-4" data-testid="game-board-page" data-kind="mancala">
      <style>{`@keyframes awwyeah-bead-drop { from { transform: translate(-50%,-140%) scale(1.35); opacity: .2 } to { transform: translate(-50%,-50%) scale(1); opacity: 1 } }
@keyframes awwyeah-flash { 0% { transform: translate(-50%,-50%) scale(.6); opacity: 0 } 18% { transform: translate(-50%,-50%) scale(1.08); opacity: 1 } 80% { opacity: 1 } 100% { transform: translate(-50%,-60%) scale(1); opacity: 0 } }`}</style>
      <GameHeader
        title={meta.title}
        status={statusLine()}
        spectating={!isPlayer}
        myTurn={myTurn}
        finished={finished}
      >
        <ReplayEntryButtons
          canWatchReplay={finished && live && moves.length > 0}
          canSeeHistory={!finished && live && moves.length > 0}
          onWatchReplay={() => {
            dispatchScrub({ type: 'start-scrub', frame: 0 });
            dispatchScrub({ type: 'play' });
          }}
          onSeeHistory={() => dispatchScrub({ type: 'start-scrub', frame: maxFrame })}
        />
      </GameHeader>

      {isPlayer && (
        <SideLine
          color={colorOf(me)}
          detail="bottom row"
          turnText={statusLine()}
          turnColor={!finished && state.turn !== null ? colorOf(state.turn) : null}
          myTurn={myTurn}
        />
      )}

      {scrub.mode === 'scrubbing' && (
        <ReplayControls
          scrub={scrub}
          dispatch={dispatchScrub}
          maxFrame={maxFrame}
          finished={finished}
          positionLabel={
            scrub.frame === 0
              ? 'Start'
              : `Move ${scrub.frame} of ${maxFrame}${frameAt?.at ? ` · ${new Date(frameAt.at).toLocaleString()}` : ''}`
          }
        />
      )}

      {nameChip(them)}
      <div
        className="relative mx-auto grid w-full max-w-[40rem] grid-cols-[1.15fr_repeat(6,1fr)_1.15fr] gap-x-1.5 rounded-[2rem] px-2.5 py-1.5 sm:gap-x-2.5 sm:px-4"
        style={{ ...WOOD, ['--bead' as string]: 'clamp(8px, 2.5vw, 14px)' }}
        data-testid="mancala-board"
      >
        {storeBox(them)}
        {cols.map((c) => pitButton(mancalaPitSlot(them, 5 - c), them))}
        {storeBox(me)}
        {cols.map((c) => pitButton(mancalaPitSlot(me, c), me))}

        {flash && (
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 rounded-full px-4 py-2 font-display text-xl font-semibold"
            style={{
              background: 'rgba(255,240,215,.96)',
              color: '#4a250e',
              boxShadow: '0 6px 18px rgba(0,0,0,.35)',
              animation: 'awwyeah-flash 1300ms ease-out both',
            }}
            data-testid="mancala-flash"
          >
            {flash}
          </div>
        )}
      </div>

      {nameChip(me)}

      <p className="min-h-[1.25rem] text-center text-sm text-fg-2" data-testid="mancala-tip">
        {animating
          ? 'Tap the board to skip'
          : (tip ??
            (actor !== null
              ? sharing && actor !== me
                ? `${nameOf(actor)}: pick a pit on the top row`
                : 'Pick one of your pits along the bottom. ★ = free turn'
              : ''))}
      </p>

      {finished && live && (
        <div className="flex flex-col items-center gap-1 rounded-md border border-line bg-surface p-3 text-center">
          <p className="font-display text-lg text-fg">
            {state.winner === null
              ? 'A tie! Perfectly shared.'
              : `${state.winner === me && isPlayer ? 'You' : nameOf(state.winner)} won!`}
          </p>
          <p className="text-sm text-fg-2 tabular-nums">
            {nameOf(0)} {state.board[6]} · {nameOf(1)} {state.board[13]}
          </p>
        </div>
      )}

      <div className="flex items-center justify-center gap-2">
        {isPlayer && !finished && (
          <button
            type="button"
            data-testid="mancala-shared-toggle"
            aria-pressed={sharedPhone}
            onClick={() => {
              const next = !sharedPhone;
              setSharedPhone(next);
              writePref(sharedKey(meta.id), next);
            }}
            className={`flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium ${
              sharedPhone
                ? 'border-line-strong bg-inverse text-inverse-fg'
                : 'border-line text-fg-2'
            }`}
          >
            <Smartphone size={16} aria-hidden="true" />
            {sharedPhone ? 'Sharing this phone' : 'Play on one phone'}
          </button>
        )}
        <button
          type="button"
          aria-label={soundOn ? 'Sound on' : 'Sound off'}
          aria-pressed={soundOn}
          onClick={() => {
            unlockAudio();
            const next = !soundOn;
            setSoundOn(next);
            writePref(SOUND_KEY, next);
          }}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg-2"
        >
          {soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
      </div>

      {finished && isPlayer && !isGuestId(userId) && live && (
        <button
          type="button"
          data-testid="rematch-button"
          onClick={rematch}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Rematch
        </button>
      )}
    </div>
  );
}
